import { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Tooltip, FormControlLabel, Checkbox } from '@mui/material';
import EnumSelector from './EnumSelector';
import { unique, getMinMax } from '../../../utils/collections';
import { percentileColor } from '../../util/data-table';
import { PredictionsChip } from '../../util/DataTableCells';

const TYPE = 'Point Type Selector';
const Selector = ({
  entities,
  onChange,

  pointTypes,
  measurements,
}) => {
  const points = entities;
  const [showInvalid, setShowInvalid] = useState(false);
  const [predictionsOnly, setPredictionsOnly] = useState(true);

  const [typePredictions, pts] = useMemo(() => {
    const unitsObserved = unique(points.map((p) => p.rawUnitId).filter(Boolean));
    const measurementPredictions = Object.values(measurements).filter((m) =>
      m.units.some((u) => unitsObserved.includes(u.id))
    );
    const predictions = getMinMax(
      points.map((p) => p.data?.['auto_tagger.point_type_preds'] ?? {}),
      true
    );
    let pTypes = Object.values(pointTypes);
    if (!showInvalid)
      pTypes = pTypes.filter((pt) => measurementPredictions.some((m) => m.id === pt.measurementId));
    if (predictionsOnly)
      pTypes = pTypes.filter((pt) => Object.prototype.hasOwnProperty.call(predictions, pt.name));
    return [predictions, Object.fromEntries(pTypes.map((pt) => [pt.id, pt]))];
  }, [points, measurements, pointTypes, showInvalid, predictionsOnly]);
  return (
    <>
      <EnumSelector
        entities={points}
        onChange={onChange}
        name="Point Type"
        enums={pts}
        extractEnumId={(p) => p.pointTypeId}
        enumId={(pt) => pt.id}
        enumLabel={(pt) => pt.name}
        renderBadge={(pt) => (
          <PredictionsChip
            color={percentileColor(typePredictions[pt.name]?.max)}
            predictions={typePredictions[pt.name]}
          />
        )}
        sortFn={(ptA, ptB) => {
          const predA = typePredictions[ptA.name];
          const predB = typePredictions[ptB.name];
          if (predA) {
            if (predB) return predB.max - predA.max;
            return -1;
          }
          if (predB) return 1;
          return ptB.name.localeCompare(ptA.name);
        }}
      />
      <FormControlLabel
        label={
          <Tooltip title="A point type is invalid if it's measurement does not match the point's unit">
            Show Invalid
          </Tooltip>
        }
        control={
          <Checkbox
            checked={showInvalid}
            onChange={(event) => setShowInvalid(event.target.checked)}
          />
        }
      />
      <FormControlLabel
        label="Predictions Only"
        control={
          <Checkbox
            checked={predictionsOnly}
            onChange={(event) => setPredictionsOnly(event.target.checked)}
          />
        }
      />
    </>
  );
};

const mapStateToProps = (state) => ({
  pointTypes: state.entities.pointTypes.byId,
  measurements: state.entities.measurements.byId,
});

const PointTypeSelector = connect(mapStateToProps)(Selector);

PointTypeSelector.displayName = TYPE;

export default PointTypeSelector;
